import { useState } from 'react';
import { usePlans, SubscriptionPlan } from '@/hooks/usePlans';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, Users, Link, BarChart3, Database, Target, Upload, Headphones, DollarSign, Crown, CreditCard, X, Package } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { PlanPaymentSelector } from './PlanPaymentSelector';

interface PlanManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PlanManagementDialog({ open, onOpenChange }: PlanManagementDialogProps) {
  const { plans, isLoading } = usePlans();
  const { tenant, currentPlan } = useUserProfile();
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPaymentSelector, setShowPaymentSelector] = useState(false);
  const [planForPayment, setPlanForPayment] = useState<SubscriptionPlan | null>(null);

  const updatePlanMutation = useMutation({
    mutationFn: async (newPlanId: string) => {
      if (!tenant?.id) throw new Error('Tenant não encontrado');
      
      const { error } = await supabase
        .from('tenants')
        .update({ subscription_plan_id: newPlanId })
        .eq('id', tenant.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      queryClient.invalidateQueries({ queryKey: ['current_subscription_plan'] });
      toast.success('Plano atualizado com sucesso!');
      setShowConfirmDialog(false);
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error('Erro ao atualizar plano: ' + error.message);
    },
  });

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    if (plan.id === currentPlan?.id) return;
    
    const isFree = plan.price_monthly === 0 && plan.price_annual === 0 && plan.price_lifetime === 0;
    const hasStripeIds = plan.stripe_price_monthly_id || plan.stripe_price_annual_id || plan.stripe_price_lifetime_id;

    if (isFree) {
      // For free plans, directly confirm the change
      setSelectedPlan(plan);
      setShowConfirmDialog(true);
    } else if (hasStripeIds) {
      // For paid plans with Stripe IDs, show payment selector
      setPlanForPayment(plan);
      setShowPaymentSelector(true);
    } else {
      // For paid plans without Stripe IDs, show contact message
      toast.info('Entre em contato para contratar este plano');
    }
  };

  const handleConfirmChange = () => {
    if (selectedPlan) {
      updatePlanMutation.mutate(selectedPlan.id);
    }
  };

  const activePlans = plans?.filter(p => p.is_active) || [];

  const calculateSavings = (monthlyPrice: number, annualPrice: number) => {
    const yearlyFromMonthly = monthlyPrice * 12;
    const savings = yearlyFromMonthly - annualPrice;
    const percentage = (savings / yearlyFromMonthly) * 100;
    return { savings, percentage };
  };

  interface FeatureItemProps {
    included: boolean;
    label: string;
  }

  const FeatureItem = ({ included, label }: FeatureItemProps) => (
    <div className={`flex items-center gap-2 ${included ? 'text-foreground' : 'text-muted-foreground/50'}`}>
      {included ? (
        <CheckCircle2 className="h-4 w-4 text-primary" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground/40" />
      )}
      <span className={included ? '' : 'line-through'}>{label}</span>
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[95vw] lg:max-w-[1400px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Gerenciar Plano</DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              Seu plano atual: 
              {currentPlan && (
                <Badge variant="default" className="ml-1">
                  <Crown className="h-3 w-3 mr-1" />
                  {currentPlan.name}
                </Badge>
              )}
              {!currentPlan && <span className="text-muted-foreground">Nenhum plano ativo</span>}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mt-4">
              {activePlans.map((plan) => {
                const isCurrent = plan.id === currentPlan?.id;
                
                return (
                  <Card key={plan.id} className={`relative ${isCurrent ? 'border-primary shadow-lg' : ''}`}>
                    {isCurrent && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-primary">Plano Atual</Badge>
                      </div>
                    )}
                    
                    <CardHeader>
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      <CardDescription className="min-h-[80px]">
                        <div className="flex items-baseline gap-1 mt-2">
                          <DollarSign className="h-4 w-4" />
                          <span className="text-2xl font-bold text-foreground">
                            R$ {plan.price_monthly.toFixed(2)}
                          </span>
                          <span className="text-muted-foreground">/mês</span>
                        </div>
                        
                        {plan.price_monthly > 0 && (
                          <div className="text-xs mt-1 space-y-0.5">
                            {plan.price_annual > 0 && (() => {
                              const { savings, percentage } = calculateSavings(plan.price_monthly, plan.price_annual);
                              return (
                                <div className="space-y-0.5">
                                  <div>Anual: R$ {plan.price_annual.toFixed(2)}</div>
                                  {savings > 0 && (
                                    <div className="text-green-600 font-medium">
                                      Economize R$ {savings.toFixed(2)} (-{percentage.toFixed(0)}%) no pagamento anual
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            
                            {plan.price_lifetime > 0 && (
                              <div>
                                Vitalício: R$ {plan.price_lifetime.toFixed(2)}
                              </div>
                            )}
                          </div>
                        )}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      {/* Limites */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {plan.max_users === 0 ? 'Usuários ilimitados' : `Até ${plan.max_users} ${plan.max_users === 1 ? 'usuário' : 'usuários'}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {(plan as any).max_products === 0 ? 'Produtos ilimitados' : `Até ${(plan as any).max_products} ${(plan as any).max_products === 1 ? 'produto' : 'produtos'}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {plan.integrations_mode === 'customizable'
                              ? `Até ${plan.max_integrations} integração(ões)`
                              : `${plan.allowed_integrations.length} integrações`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4 text-muted-foreground" />
                          <span>{plan.basic_metrics.length} métricas básicas</span>
                        </div>
                      </div>

                      <Separator />

                      {/* Features - Todas as features com indicadores visuais */}
                      <div className="space-y-2 text-sm min-h-[160px]">
                        <FeatureItem 
                          included={plan.advanced_metrics_enabled}
                          label="Métricas avançadas"
                        />
                        
                        <FeatureItem 
                          included={plan.goals_enabled}
                          label={plan.goals_enabled 
                            ? (plan.max_goals === 0 ? 'Metas ilimitadas' : `Até ${plan.max_goals} metas`)
                            : 'Metas'
                          }
                        />

                        <FeatureItem 
                          included={plan.manual_entry_enabled}
                          label="Entrada manual"
                        />
                        
                        <FeatureItem 
                          included={plan.bulk_import_enabled}
                          label="Importação em massa"
                        />
                        
                        <FeatureItem 
                          included={plan.priority_support}
                          label="Suporte prioritário"
                        />
                      </div>

                      <Separator />

                      {/* Action Button */}
                      <Button 
                        onClick={() => handleSelectPlan(plan)}
                        disabled={isCurrent || updatePlanMutation.isPending}
                        variant={isCurrent ? 'secondary' : 'default'}
                        className="w-full"
                      >
                        {isCurrent ? (
                          'Plano Atual'
                        ) : (
                          <>
                            {(plan.price_monthly > 0 || plan.price_annual > 0 || plan.price_lifetime > 0) && (
                              <CreditCard className="mr-2 h-4 w-4" />
                            )}
                            Selecionar Plano
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {activePlans.length === 0 && !isLoading && (
            <div className="text-center py-12 text-muted-foreground">
              <p>Nenhum plano disponível no momento.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar mudança de plano</AlertDialogTitle>
            <AlertDialogDescription>
              Você está prestes a {selectedPlan && currentPlan && selectedPlan.price_monthly > currentPlan.price_monthly ? 'fazer upgrade' : 'fazer downgrade'} para o plano <strong>{selectedPlan?.name}</strong>.
              {selectedPlan && (
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <div className="text-sm space-y-1">
                    <div>Valor mensal: <strong>R$ {selectedPlan.price_monthly.toFixed(2)}</strong></div>
                    <div>Valor anual: <strong>R$ {selectedPlan.price_annual.toFixed(2)}</strong></div>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmChange} disabled={updatePlanMutation.isPending}>
              {updatePlanMutation.isPending ? 'Atualizando...' : 'Confirmar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {planForPayment && (
        <PlanPaymentSelector
          open={showPaymentSelector}
          onOpenChange={setShowPaymentSelector}
          plan={planForPayment}
        />
      )}
    </>
  );
}
